import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import {
  getUserProfile, updateUserProfile,
  sendConnectionRequest, isConnected, getUserMetrics,
  disconnectSocialAccount, connectInstagramAccount, parseInstagramUsername,
  connectXAccount, parseXUsername,
} from "../services/userService";
import { logOut } from "../services/authService";
import { loginWithSpotify, clearSpotifyToken } from "../services/spotifyService";

export default function ProfileScreen({ uid, onBack, onOpenChat, onNotif, onLogout }) {
  const { user, profile: myProfile, refreshProfile, isGuest } = useAuth();
  const isOwnProfile = uid === user?.uid;
  const [profile, setProfile] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [editing, setEditing] = useState(false);
  const [connected, setConnected] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [privacy, setPrivacy] = useState("public");
  const [loading, setLoading] = useState(true);
  const [showSpotifyMenu, setShowSpotifyMenu] = useState(false);
  const [showInstagramMenu, setShowInstagramMenu] = useState(false);
  const [instagramInput, setInstagramInput] = useState("");
  const [showXMenu, setShowXMenu] = useState(false);
  const [xInput, setXInput] = useState("");

  useEffect(() => {
    if (!uid) return;
    loadProfile();
  }, [uid]);

  const loadProfile = async () => {
    setLoading(true);
    const p = isOwnProfile ? myProfile : await getUserProfile(uid);
    setProfile(p);
    if (p) {
      setDisplayName(p.displayName || "");
      setBio(p.bio || "");
      setPrivacy(p.privacy || "public");
      const m = await getUserMetrics(uid);
      setMetrics(m);
      if (!isOwnProfile && user && !user.isGuest) {
        const conn = await isConnected(user.uid, uid);
        setConnected(conn);
      }
    }
    setLoading(false);
  };

  const handleSave = async () => {
    await updateUserProfile(uid, { displayName, bio, privacy });
    await refreshProfile();
    setEditing(false);
    onNotif("Profile updated ✓");
  };

  const handleConnect = async () => {
    if (!user || user.isGuest) { onNotif("Sign in to connect with others", "error"); return; }
    await sendConnectionRequest(user.uid, uid);
    setRequestSent(true);
    onNotif("Connection request sent ✓");
  };

  const handleLogout = async () => {
    if (isGuest) {
      onLogout();
    } else {
      await logOut();
      onLogout();
    }
  };

  const handleSpotifyClick = () => {
    if (profile.connectedAccounts?.spotify?.connected) {
      setShowSpotifyMenu(true);
    } else {
      loginWithSpotify("profile");
    }
  };

  const handleInstagramClick = () => {
    setInstagramInput(profile.connectedAccounts?.instagram?.username || "");
    setShowInstagramMenu(true);
  };

  const handleSaveInstagram = async () => {
    if (!parseInstagramUsername(instagramInput)) {
      onNotif("Enter a valid Instagram username", "error");
      return;
    }
    await connectInstagramAccount(uid, instagramInput);
    setShowInstagramMenu(false);
    await refreshProfile();
    await loadProfile();
    onNotif("Instagram linked ✓");
  };

  const handleUnlinkInstagram = async () => {
    await disconnectSocialAccount(uid, "instagram");
    setShowInstagramMenu(false);
    await refreshProfile();
    await loadProfile();
    onNotif("Instagram removed");
  };

  const handleXClick = () => {
    setXInput(profile.connectedAccounts?.x?.username || "");
    setShowXMenu(true);
  };

  const handleSaveX = async () => {
    if (!parseXUsername(xInput)) {
      onNotif("Enter a valid X username", "error");
      return;
    }
    await connectXAccount(uid, xInput);
    setShowXMenu(false);
    await refreshProfile();
    await loadProfile();
    onNotif("X linked ✓");
  };

  const handleUnlinkX = async () => {
    await disconnectSocialAccount(uid, "x");
    setShowXMenu(false);
    await refreshProfile();
    await loadProfile();
    onNotif("X removed");
  };

  const handleUnlinkSpotify = async () => {
    await disconnectSocialAccount(uid, "spotify");
    clearSpotifyToken();
    setShowSpotifyMenu(false);
    await refreshProfile();
    await loadProfile();
    onNotif("Spotify disconnected");
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0A0A0A", fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 14 }}>Loading profile...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0A0A0A", fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 14 }}>Profile not found</div>
      </div>
    );
  }

  const isPrivate = profile.privacy === "private" && !isOwnProfile;
  const xAccount = profile.connectedAccounts?.x;
  const xUrl = xAccount?.connected && xAccount.username
    ? `https://x.com/${encodeURIComponent(xAccount.username)}`
    : null;
  const instagramAccount = profile.connectedAccounts?.instagram;
  const instagramUrl = instagramAccount?.connected && instagramAccount.username
    ? `https://www.instagram.com/${encodeURIComponent(instagramAccount.username)}/`
    : null;
  const spotifyAccount = profile.connectedAccounts?.spotify;
  // Older links only stored the Spotify id, so fall back to building the URL
  const spotifyUrl = spotifyAccount?.connected
    ? (spotifyAccount.profileUrl || (spotifyAccount.id ? `https://open.spotify.com/user/${encodeURIComponent(spotifyAccount.id)}` : null))
    : null;

  return (
    <div style={{ minHeight: "100vh", background: "#0A0A0A", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Serif+Display:ital@0;1&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 0; }
        .profile-header { padding: 80px 40px 40px; position: relative; overflow: hidden; max-width: 900px; margin: 0 auto; text-align: center; }
        .profile-header-bg { position: absolute; inset: 0; background: radial-gradient(ellipse at 50% 30%, rgba(29,185,84,0.08) 0%, transparent 60%); pointer-events: none; }
        .profile-avatar { width: 100px; height: 100px; border-radius: 50%; object-fit: cover; border: 3px solid rgba(255,255,255,0.2); }
        .profile-avatar-placeholder { width: 100px; height: 100px; border-radius: 50%; background: rgba(255,255,255,0.1); display: flex; align-items: center; justify-content: center; font-size: 40px; border: 3px solid rgba(255,255,255,0.15); }
        .profile-name { font-family: 'DM Serif Display', serif; font-size: clamp(32px, 4vw, 48px); color: #ffffff; letter-spacing: -2px; margin-top: 16px; }
        .profile-bio { font-size: 13px; color: rgba(255,255,255,0.5); margin-top: 4px; line-height: 1.6; }
        .profile-badges { display: flex; gap: 8px; margin-top: 12px; flex-wrap: wrap; justify-content: center; }
        .badge { padding: 4px 12px; border-radius: 100px; font-size: 11px; font-weight: 700; }
        .badge-public { background: rgba(29,185,84,0.1); color: #1DB954; border: 1px solid rgba(29,185,84,0.3); }
        .badge-private { background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.5); border: 1px solid rgba(255,255,255,0.15); }
        .profile-actions { display: flex; gap: 8px; margin-top: 16px; position: relative; z-index: 1; justify-content: center; }
        .profile-btn { flex: 1; padding: 12px; border-radius: 100px; border: none; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
        .profile-btn:active { transform: scale(0.97); }
        .profile-btn-primary { background: #ffffff; color: #0A0A0A; font-weight: 700; }
        .profile-btn-primary:hover { background: rgba(255,255,255,0.9); transform: translateY(-1px); }
        .profile-btn-secondary { background: none; color: #ffffff; border: 1.5px solid rgba(255,255,255,0.2); }
        .profile-btn-secondary:hover { border-color: rgba(255,255,255,0.5); background: rgba(255,255,255,0.05); }
        .profile-back { background: none; border: none; color: rgba(255,255,255,0.5); font-size: 13px; cursor: pointer; font-family: 'DM Sans', sans-serif; transition: color 0.2s; position: relative; z-index: 1; }
        .profile-back:hover { color: rgba(255,255,255,0.8); }
        .profile-edit { background: none; border: 1px solid rgba(255,255,255,0.15); border-radius: 100px; color: rgba(255,255,255,0.7); font-size: 12px; font-weight: 600; padding: 6px 14px; cursor: pointer; font-family: 'DM Sans', sans-serif; transition: all 0.2s; position: relative; z-index: 1; }
        .profile-edit:hover { border-color: rgba(255,255,255,0.3); color: #ffffff; }
        .profile-section { padding: 24px 40px 0; max-width: 900px; margin: 0 auto; }
        .profile-section-title { font-size: 12px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #1DB954; margin-bottom: 14px; }
        .metric-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 20px; padding: 14px 16px; margin-bottom: 8px; display: flex; align-items: center; gap: 12px; transition: all 0.2s; }
        .metric-card:hover { background: rgba(255,255,255,0.06); border-color: rgba(255,255,255,0.15); }
        .metric-cover { width: 44px; height: 44px; border-radius: 10px; object-fit: cover; flex-shrink: 0; }
        .metric-title { font-size: 13px; font-weight: 600; color: #ffffff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .metric-sub { font-size: 11px; color: rgba(255,255,255,0.4); margin-top: 2px; }
        .metric-count { font-size: 13px; font-weight: 700; color: rgba(255,255,255,0.5); }
        .edit-input { width: 100%; padding: 14px 16px; border-radius: 12px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); font-family: 'DM Sans', sans-serif; font-size: 14px; color: #ffffff; outline: none; margin-bottom: 10px; transition: all 0.2s; }
        .edit-input:focus { border-color: rgba(255,255,255,0.3); }
        .edit-input::placeholder { color: rgba(255,255,255,0.3); }
        .privacy-toggle { display: flex; gap: 8px; margin-bottom: 16px; }
        .privacy-btn { flex: 1; padding: 10px; border-radius: 100px; border: 1.5px solid rgba(255,255,255,0.15); background: none; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; color: rgba(255,255,255,0.5); transition: all 0.2s; }
        .privacy-btn.active { background: #ffffff; color: #0A0A0A; border-color: #ffffff; }
        .connected-account { display: flex; align-items: center; gap: 12px; padding: 14px 16px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 20px; margin-bottom: 8px; cursor: pointer; transition: all 0.2s; }
        .connected-account:active { background: rgba(255,255,255,0.06); }
        .logout-btn { width: calc(100% - 40px); margin: 20px; padding: 14px; border-radius: 100px; background: rgba(255,107,107,0.1); border: 1px solid rgba(255,107,107,0.2); color: #ff6b6b; font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
        .logout-btn:active { transform: scale(0.98); }
        .guest-banner { margin: 16px 20px; padding: 20px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 20px; text-align: center; }
        .save-btn { width: 100%; padding: 14px 32px; border-radius: 100px; background: #ffffff; border: none; color: #0A0A0A; font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 700; cursor: pointer; margin-bottom: 20px; transition: all 0.2s; }
        .save-btn:active { transform: scale(0.98); }
        .unlink-btn { width: 100%; padding: 12px; border-radius: 100px; background: rgba(255,107,107,0.1); border: 1px solid rgba(255,107,107,0.2); color: #ff6b6b; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; margin-top: 8px; }
        .unlink-btn:active { transform: scale(0.98); }
        .spotify-link { text-decoration: none; }
        .spotify-link:hover { border-color: rgba(29,185,84,0.4); background: rgba(29,185,84,0.08); }
        .spotify-menu-link { display: block; width: 100%; padding: 12px; border-radius: 100px; background: rgba(29,185,84,0.12); border: 1px solid rgba(29,185,84,0.3); color: #1DB954; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; text-align: center; text-decoration: none; transition: all 0.2s; }
        .spotify-menu { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 16px; padding: 12px; margin-bottom: 8px; }
        .spotify-menu-cancel { width: 100%; padding: 10px; border-radius: 100px; background: none; border: 1.5px solid rgba(255,255,255,0.15); color: rgba(255,255,255,0.6); font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
      `}</style>

      <div className="profile-header">
        <div className="profile-header-bg" />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <button className="profile-back" onClick={onBack}>← Back</button>
          {isOwnProfile && !isGuest && (
            <button className="profile-edit" onClick={() => setEditing(!editing)}>
              {editing ? "Cancel" : "Edit"}
            </button>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "flex-end", gap: 16, marginTop: 12, position: "relative", zIndex: 1 }}>
          <div>
            {profile.photoURL
              ? <img src={profile.photoURL} alt="" className="profile-avatar" />
              : <div className="profile-avatar-placeholder">
                  {isGuest ? "👤" : (profile.displayName?.[0]?.toUpperCase() || "👤")}
                </div>
            }
          </div>
          <div style={{ flex: 1 }}>
            <div className="profile-name">{profile.displayName || "User"}</div>
            {profile.bio && <div className="profile-bio">{profile.bio}</div>}
            <div className="profile-badges">
              {isGuest ? (
                <span className="badge badge-private">👤 Guest</span>
              ) : (
                <span className={`badge ${profile.privacy === "public" ? "badge-public" : "badge-private"}`}>
                  {profile.privacy === "public" ? "🌍 Public" : "🔒 Private"}
                </span>
              )}
              {profile.connectedAccounts?.spotify && (
                <span className="badge badge-public">🎵 Spotify</span>
              )}
            </div>
          </div>
        </div>

        {!isOwnProfile && !isPrivate && (
          <div className="profile-actions">
            {connected ? (
              <button className="profile-btn profile-btn-primary" onClick={() => onOpenChat(profile)}>💬 Message</button>
            ) : requestSent ? (
              <button className="profile-btn profile-btn-secondary" disabled>Request Sent</button>
            ) : (
              <button className="profile-btn profile-btn-primary" onClick={handleConnect}>🤝 Connect</button>
            )}
            <button className="profile-btn profile-btn-secondary">Share Profile</button>
          </div>
        )}
      </div>

      {isOwnProfile && isGuest && (
        <div className="guest-banner">
          <div style={{ fontSize: 28, marginBottom: 8 }}>👤</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#ffffff", marginBottom: 4, letterSpacing: -0.3 }}>You're browsing as a Guest</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 16, lineHeight: 1.6 }}>Create an account to save your history, connect with others, and unlock your full profile.</div>
          <button className="upgrade-btn" onClick={onLogout}>
            Create Account
          </button>
        </div>
      )}

      {editing && isOwnProfile && !isGuest && (
        <div className="profile-section">
          <div className="profile-section-title">Edit Profile</div>
          <input
            className="edit-input"
            placeholder="Display name"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
          />
          <textarea
            className="edit-input"
            placeholder="Bio — tell people about your taste in music"
            value={bio}
            onChange={e => setBio(e.target.value)}
            rows={3}
            style={{ resize: "none" }}
          />
          <div className="profile-section-title" style={{ marginBottom: 8 }}>Privacy</div>
          <div className="privacy-toggle">
            <button className={`privacy-btn ${privacy === "public" ? "active" : ""}`} onClick={() => setPrivacy("public")}>🌍 Public</button>
            <button className={`privacy-btn ${privacy === "private" ? "active" : ""}`} onClick={() => setPrivacy("private")}>🔒 Private</button>
          </div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 16, lineHeight: 1.6 }}>
            {privacy === "public"
              ? "Your profile, song metrics, and activity are visible to others at venues."
              : "Your profile is hidden from other users at venues."}
          </div>
          <button className="save-btn" onClick={handleSave}>
            Save Changes
          </button>
        </div>
      )}

      {isPrivate && (
        <div style={{ padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#ffffff", marginBottom: 8, letterSpacing: -0.3 }}>Private Profile</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.5 }}>This user's profile is private.</div>
        </div>
      )}

      {!isPrivate && !isGuest && metrics?.mostRequested?.length > 0 && (
        <div className="profile-section">
          <div className="profile-section-title">Most Requested Songs</div>
          {metrics.mostRequested.map((song) => (
            <div key={song.id} className="metric-card">
              {song.cover && <img src={song.cover} alt="" className="metric-cover" />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="metric-title">{song.title}</div>
                <div className="metric-sub">{song.artist}</div>
              </div>
              <div className="metric-count">{song.count}×</div>
            </div>
          ))}
        </div>
      )}

      {!isOwnProfile && !isPrivate && spotifyUrl && (
        <div className="profile-section">
          <div className="profile-section-title">On Spotify</div>
          <a className="connected-account spotify-link" href={spotifyUrl} target="_blank" rel="noopener noreferrer">
            <span style={{ fontSize: 20 }}>🎵</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#ffffff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {spotifyAccount.displayName || profile.displayName || "Spotify profile"}
              </div>
              <div style={{ fontSize: 11, color: "#1DB954" }}>View Spotify profile</div>
            </div>
            <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 14 }}>↗</span>
          </a>
        </div>
      )}

      {!isOwnProfile && !isPrivate && xUrl && (
        <div className="profile-section">
          <div className="profile-section-title" style={{ color: "#ffffff" }}>On X</div>
          <a className="connected-account spotify-link" href={xUrl} target="_blank" rel="noopener noreferrer">
            <span style={{ fontSize: 20 }}>𝕏</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#ffffff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                @{xAccount.username}
              </div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>View X profile</div>
            </div>
            <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 14 }}>↗</span>
          </a>
        </div>
      )}

      {!isOwnProfile && !isPrivate && instagramUrl && (
        <div className="profile-section">
          <div className="profile-section-title" style={{ color: "#E1306C" }}>On Instagram</div>
          <a className="connected-account spotify-link" href={instagramUrl} target="_blank" rel="noopener noreferrer">
            <span style={{ fontSize: 20 }}>📸</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#ffffff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                @{instagramAccount.username}
              </div>
              <div style={{ fontSize: 11, color: "#E1306C" }}>View Instagram profile</div>
            </div>
            <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 14 }}>↗</span>
          </a>
        </div>
      )}

      {!isPrivate && !isGuest && isOwnProfile && (!metrics?.mostRequested || metrics.mostRequested.length === 0) && !editing && (
        <div className="profile-section">
          <div className="profile-section-title">Music Activity</div>
          <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, padding: "24px 16px", textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>🎵</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#ffffff", marginBottom: 4 }}>No songs requested yet</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", lineHeight: 1.5 }}>Your most requested songs will appear here</div>
          </div>
        </div>
      )}

      {isOwnProfile && !isGuest && !editing && (
        <div className="profile-section" style={{ marginTop: 8 }}>
          <div className="profile-section-title">Connected Accounts</div>
          {showSpotifyMenu && (
            <div className="spotify-menu">
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginBottom: 10, textAlign: "center" }}>
                Connected as {profile.connectedAccounts?.spotify?.displayName}
              </div>
              {spotifyUrl && (
                <a className="spotify-menu-link" href={spotifyUrl} target="_blank" rel="noopener noreferrer">
                  View my Spotify profile ↗
                </a>
              )}
              <button className="unlink-btn" onClick={handleUnlinkSpotify}>Unlink Spotify</button>
              <button className="spotify-menu-cancel" onClick={() => setShowSpotifyMenu(false)} style={{ marginTop: 8 }}>
                Cancel
              </button>
            </div>
          )}
          {showInstagramMenu && (
            <div className="spotify-menu">
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginBottom: 10, textAlign: "center" }}>
                Your Instagram username
              </div>
              <input
                className="edit-input"
                placeholder="@username or instagram.com link"
                value={instagramInput}
                onChange={e => setInstagramInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSaveInstagram()}
                autoFocus
              />
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", marginBottom: 10, lineHeight: 1.5 }}>
                Shown on your profile as a link. Tapped doesn't verify it's your account.
              </div>
              <button className="save-btn" onClick={handleSaveInstagram}>Save</button>
              {instagramUrl && (
                <>
                  <a className="spotify-menu-link" href={instagramUrl} target="_blank" rel="noopener noreferrer" style={{ marginTop: 8 }}>
                    View my Instagram ↗
                  </a>
                  <button className="unlink-btn" onClick={handleUnlinkInstagram}>Remove Instagram</button>
                </>
              )}
              <button className="spotify-menu-cancel" onClick={() => setShowInstagramMenu(false)} style={{ marginTop: 8 }}>
                Cancel
              </button>
            </div>
          )}
          {showXMenu && (
            <div className="spotify-menu">
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginBottom: 10, textAlign: "center" }}>
                Your X username
              </div>
              <input
                className="edit-input"
                placeholder="@username or x.com link"
                value={xInput}
                onChange={e => setXInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSaveX()}
                autoFocus
              />
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", marginBottom: 10, lineHeight: 1.5 }}>
                Shown on your profile as a link. Tapped doesn't verify it's your account.
              </div>
              <button className="save-btn" onClick={handleSaveX}>Save</button>
              {xUrl && (
                <>
                  <a className="spotify-menu-link" href={xUrl} target="_blank" rel="noopener noreferrer" style={{ marginTop: 8 }}>
                    View my X profile ↗
                  </a>
                  <button className="unlink-btn" onClick={handleUnlinkX}>Remove X</button>
                </>
              )}
              <button className="spotify-menu-cancel" onClick={() => setShowXMenu(false)} style={{ marginTop: 8 }}>
                Cancel
              </button>
            </div>
          )}
          {[
            { key: "spotify", icon: "🎵", label: "Spotify", color: "#1DB954", onClick: handleSpotifyClick },
            { key: "instagram", icon: "📸", label: "Instagram", color: "#E1306C", onClick: handleInstagramClick },
            { key: "x", icon: "𝕏", label: "X (Twitter)", color: "#ffffff", onClick: handleXClick },
            { key: "facebook", icon: "👤", label: "Facebook", color: "#1877F2" },
          ].map(({ key, icon, label, color, onClick }) => {
            const account = profile.connectedAccounts?.[key];
            const isSpotify = key === "spotify";
            const isActive = isSpotify || key === "instagram" || key === "x";
            return (
              <div
                key={key}
                className="connected-account"
                onClick={onClick || undefined}
                style={{ cursor: isActive ? "pointer" : "default", opacity: isActive ? 1 : 0.5 }}
              >
                <span style={{ fontSize: 20 }}>{icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#ffffff" }}>{label}</div>
                  <div style={{ fontSize: 11, color: account?.connected ? color : "rgba(255,255,255,0.4)" }}>
                    {account?.connected
                      ? (isActive ? `Connected as ${account.displayName}` : "Connected")
                      : (isActive ? "Tap to connect" : "Coming soon")}
                  </div>
                </div>
                {account?.connected
                  ? <span style={{ color: "#1DB954", fontSize: 13, fontWeight: 600 }}>✓</span>
                  : isActive
                    ? <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 14 }}>+</span>
                    : null
                }
              </div>
            );
          })}
        </div>
      )}

      {isOwnProfile && (
        <button className="logout-btn" onClick={handleLogout}>
          {isGuest ? "Exit Guest Mode" : "Sign Out"}
        </button>
      )}

      <div style={{ height: 20 }} />
    </div>
  );
}
